import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  alpha,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery
} from '@mui/material';
import {
  AddRounded,
  AutoAwesomeRounded,
  CollectionsRounded,
  DeleteOutlineRounded,
  EditRounded,
  MapRounded,
  OpenInNewRounded,
  RouteRounded
} from '@mui/icons-material';
import { FlowBreadcrumbs, FlowPageHeader, FlowStatGrid, SoftPanel, truncateText } from './FlowLayout';

const slugifySegment = (value) =>
  String(value || '')
    .replace(/<[^>]*>/g, '')
    .trim()
    .replace(/\s+/g, '-');

const ManageDestination = () => {
  const { tourTypeId } = useParams();
  const isMobile = useMediaQuery('(max-width:900px)');
  const [destinations, setDestinations] = useState([]);
  const [search, setSearch] = useState('');
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [selectedDestinationId, setSelectedDestinationId] = useState(null);
  const [continentName, setContinentName] = useState('');
  const [tourTypeName, setTourTypeName] = useState('');
  const [continentId, setContinentId] = useState('');

  useEffect(() => {
    axios.get(`https://aaryanholidays.in/backend/destinations?tour_type_id=${tourTypeId}`)
      .then((response) => setDestinations(response.data))
      .catch((error) => console.error('There was an error fetching the destinations!', error));

    axios.get(`https://aaryanholidays.in/backend/tour-types/${tourTypeId}`)
      .then((response) => {
        setTourTypeName(response.data.tour_type);
        setContinentId(response.data.continent);
        return axios.get(`https://aaryanholidays.in/backend/continents/${response.data.continent}`);
      })
      .then((response) => setContinentName(response.data.continent))
      .catch((error) => console.error('Error fetching hierarchy:', error));
  }, [tourTypeId]);

  const handleDelete = (id) => {
    axios.delete(`https://aaryanholidays.in/backend/destinations/${id}`)
      .then(() => setDestinations((current) => current.filter((dest) => dest.id !== id)))
      .catch((error) => console.error('There was an error deleting the destination!', error));
    setOpenConfirmDialog(false);
    setSelectedDestinationId(null);
  };

  const filteredDestinations = useMemo(() => {
    return destinations.filter((dest) =>
      String(dest.thumbnail_heading || '').toLowerCase().includes(search.toLowerCase())
    );
  }, [destinations, search]);

  const getPreviewUrl = (destination) => {
    const continentSlug = slugifySegment(continentName);
    const countrySlug = slugifySegment(tourTypeName);
    const packageSlug = slugifySegment(destination.thumbnail_heading);
    return `https://aaryanholidays.com/${continentSlug}/${countrySlug}/${packageSlug}/itenary/${destination.id}`;
  };

  return (
    <Box>
      <FlowBreadcrumbs
        items={[
          { label: 'Tour Management', to: '/manage-continents' },
          { label: continentName || 'Continent', to: continentId ? `/manage-tour-type/${continentId}` : undefined },
          { label: tourTypeName || 'Country' },
          { label: 'Packages' }
        ]}
      />

      <FlowPageHeader
        title={`${tourTypeName || 'Country'} Packages`}
        subtitle="Search, edit, and open subordinate content quickly with a cleaner package operations view."
        search={search}
        searchPlaceholder="Search package names, descriptions, locations, or tags"
        onSearchChange={setSearch}
        action={(
          <Button
            component={Link}
            to={`/manage-destination/add/${tourTypeId}`}
            variant="contained"
            startIcon={<AddRounded />}
            sx={{ borderRadius: 999, px: 2.5, py: 1.2, textTransform: 'none', fontWeight: 700 }}
          >
            Add Package
          </Button>
        )}
      />

      <FlowStatGrid
        items={[
          { label: 'Packages', value: destinations.length, icon: MapRounded, tone: 'linear-gradient(135deg, #0f766e 0%, #2dd4bf 100%)' }
        ]}
      />

      <SoftPanel>
        {isMobile ? (
          <Stack spacing={2}>
            {filteredDestinations.map((dest) => (
              <Box
                key={dest.id}
                sx={{
                  p: 2,
                  borderRadius: 4,
                  border: '1px solid rgba(148, 163, 184, 0.16)',
                  backgroundColor: '#ffffff',
                  boxShadow: '0 10px 24px rgba(15, 23, 42, 0.05)'
                }}
              >
                <Stack spacing={1.5}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                    <Box>
                      <Typography variant="overline" sx={{ color: '#64748b', letterSpacing: '0.1em' }}>
                        ID {dest.id}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
                        {dest.thumbnail_heading}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title="Edit package">
                        <IconButton component={Link} to={`/manage-destination/edit/${dest.id}`}>
                          <EditRounded />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete package">
                        <IconButton onClick={() => { setSelectedDestinationId(dest.id); setOpenConfirmDialog(true); }}>
                          <DeleteOutlineRounded />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>

                  <Typography variant="body2" sx={{ color: '#334155', lineHeight: 1.7 }}>
                    {truncateText(dest.thumbnail_desc, 120)}
                  </Typography>

                  <Stack direction="row" spacing={2} flexWrap="wrap">
                    <Typography variant="body2" sx={{ color: '#475569' }}>
                      <strong>Promote Order:</strong> {dest.promote ? (dest.promote_order || '0') : 'No'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#475569' }}>
                      <strong>Is Active:</strong> {dest.is_active_id ? 'Yes' : 'No'}
                    </Typography>
                  </Stack>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                      gap: 1
                    }}
                  >
                    <Button
                      component={Link}
                      to={`/manage-destination/itineraries/${dest.id}`}
                      startIcon={<RouteRounded />}
                      sx={{
                        borderRadius: 3,
                        textTransform: 'none',
                        fontWeight: 700,
                        justifyContent: 'flex-start',
                        px: 1.4,
                        py: 1,
                        color: '#0f172a',
                        backgroundColor: '#f8fafc',
                        border: '1px solid rgba(148, 163, 184, 0.2)'
                      }}
                    >
                      Itineraries
                    </Button>
                    <Button
                      component="a"
                      href={getPreviewUrl(dest)}
                      target="_blank"
                      rel="noreferrer"
                      startIcon={<OpenInNewRounded />}
                      sx={{
                        borderRadius: 3,
                        textTransform: 'none',
                        fontWeight: 700,
                        justifyContent: 'flex-start',
                        px: 1.4,
                        py: 1,
                        color: '#ffffff',
                        background: 'linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)',
                        boxShadow: '0 14px 28px rgba(37, 99, 235, 0.18)'
                      }}
                    >
                      Preview Live
                    </Button>
                    <Button
                      component={Link}
                      to={`/manage-destination/gallery/${dest.id}`}
                      startIcon={<CollectionsRounded />}
                      sx={{
                        borderRadius: 3,
                        textTransform: 'none',
                        fontWeight: 700,
                        justifyContent: 'flex-start',
                        px: 1.4,
                        py: 1,
                        color: '#0f172a',
                        backgroundColor: '#f8fafc',
                        border: '1px solid rgba(148, 163, 184, 0.2)'
                      }}
                    >
                      Gallery
                    </Button>
                    <Button
                      component={Link}
                      to={`/manage-special-features/${dest.id}`}
                      startIcon={<AutoAwesomeRounded />}
                      sx={{
                        borderRadius: 3,
                        textTransform: 'none',
                        fontWeight: 700,
                        justifyContent: 'flex-start',
                        px: 1.4,
                        py: 1,
                        color: '#0f172a',
                        backgroundColor: '#f8fafc',
                        border: '1px solid rgba(148, 163, 184, 0.2)'
                      }}
                    >
                      Features
                    </Button>
                  </Box>
                </Stack>
              </Box>
            ))}
          </Stack>
        ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Package</TableCell>
                <TableCell>Thumbnail Copy</TableCell>
                <TableCell>Promote Order</TableCell>
                <TableCell>Is Active</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredDestinations.map((dest) => (
                <TableRow key={dest.id} hover>
                  <TableCell>{dest.id}</TableCell>
                  <TableCell>
                    <Typography sx={{ fontWeight: 700 }}>{dest.thumbnail_heading}</Typography>
                  </TableCell>
                  <TableCell>{truncateText(dest.thumbnail_desc, 120)}</TableCell>
                  <TableCell>{dest.promote ? (dest.promote_order || '0') : 'No'}</TableCell>
                  <TableCell>{dest.is_active_id ? 'Yes' : 'No'}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="flex-start">
                      <Tooltip title="Edit package">
                        <IconButton component={Link} to={`/manage-destination/edit/${dest.id}`}>
                          <EditRounded />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete package">
                        <IconButton onClick={() => { setSelectedDestinationId(dest.id); setOpenConfirmDialog(true); }}>
                          <DeleteOutlineRounded />
                        </IconButton>
                      </Tooltip>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                          gap: 1,
                          minWidth: 270
                        }}
                      >
                        <Button
                          component={Link}
                          to={`/manage-destination/itineraries/${dest.id}`}
                          startIcon={<RouteRounded />}
                          sx={{
                            borderRadius: 3,
                            textTransform: 'none',
                            fontWeight: 700,
                            justifyContent: 'flex-start',
                            px: 1.4,
                            py: 1,
                            color: '#0f172a',
                            backgroundColor: '#f8fafc',
                            border: '1px solid rgba(148, 163, 184, 0.2)',
                            '&:hover': {
                              backgroundColor: alpha('#2563eb', 0.06),
                              borderColor: alpha('#2563eb', 0.28)
                            }
                          }}
                        >
                          Itineraries
                        </Button>
                        <Button
                          component="a"
                          href={getPreviewUrl(dest)}
                          target="_blank"
                          rel="noreferrer"
                          startIcon={<OpenInNewRounded />}
                          sx={{
                            borderRadius: 3,
                            textTransform: 'none',
                            fontWeight: 700,
                            justifyContent: 'flex-start',
                            px: 1.4,
                            py: 1,
                            color: '#ffffff',
                            background: 'linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)',
                            boxShadow: '0 14px 28px rgba(37, 99, 235, 0.18)',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #0284c7 0%, #1d4ed8 100%)'
                            }
                          }}
                        >
                          Preview Live
                        </Button>
                        <Button
                          component={Link}
                          to={`/manage-destination/gallery/${dest.id}`}
                          startIcon={<CollectionsRounded />}
                          sx={{
                            borderRadius: 3,
                            textTransform: 'none',
                            fontWeight: 700,
                            justifyContent: 'flex-start',
                            px: 1.4,
                            py: 1,
                            color: '#0f172a',
                            backgroundColor: '#f8fafc',
                            border: '1px solid rgba(148, 163, 184, 0.2)',
                            '&:hover': {
                              backgroundColor: alpha('#2563eb', 0.06),
                              borderColor: alpha('#2563eb', 0.28)
                            }
                          }}
                        >
                          Gallery
                        </Button>
                        <Button
                          component={Link}
                          to={`/manage-special-features/${dest.id}`}
                          startIcon={<AutoAwesomeRounded />}
                          sx={{
                            borderRadius: 3,
                            textTransform: 'none',
                            fontWeight: 700,
                            justifyContent: 'flex-start',
                            px: 1.4,
                            py: 1,
                            color: '#0f172a',
                            backgroundColor: '#f8fafc',
                            border: '1px solid rgba(148, 163, 184, 0.2)',
                            '&:hover': {
                              backgroundColor: alpha('#2563eb', 0.06),
                              borderColor: alpha('#2563eb', 0.28)
                            }
                          }}
                        >
                          Features
                        </Button>
                      </Box>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        )}
      </SoftPanel>

      <Dialog open={openConfirmDialog} onClose={() => setOpenConfirmDialog(false)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to delete this destination? This action cannot be undone.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmDialog(false)}>Cancel</Button>
          <Button onClick={() => handleDelete(selectedDestinationId)} autoFocus>Confirm</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageDestination;
